import { OVERVIEW_CACHE_SIZE } from './constants';

export interface OverviewEntry {
    bitmap?: ImageBitmap;
    promise?: Promise<ImageBitmap | undefined>;
}

export class OverviewCache {
    private entries = new Map<string, OverviewEntry>();

    constructor(
        private readonly release: (bitmap: ImageBitmap) => void,
        private readonly maxSize = OVERVIEW_CACHE_SIZE,
    ) {}

    has(key: string): boolean {
        return this.entries.has(key);
    }

    get(key: string): OverviewEntry | undefined {
        const entry = this.entries.get(key);
        if (entry) {
            this.entries.delete(key);
            this.entries.set(key, entry);
        }
        return entry;
    }

    setPending(key: string, promise: Promise<ImageBitmap | undefined>): void {
        this.put(key, { promise });
    }

    setBitmap(key: string, bitmap: ImageBitmap): void {
        this.put(key, { bitmap });
    }

    delete(key: string): void {
        this.entries.delete(key);
    }

    clear(): void {
        this.entries.forEach((entry) => entry.bitmap && this.release(entry.bitmap));
        this.entries.clear();
    }

    get size(): number {
        return this.entries.size;
    }

    private put(key: string, entry: OverviewEntry): void {
        const previous = this.entries.get(key);
        if (previous?.bitmap && previous.bitmap !== entry.bitmap) {
            this.release(previous.bitmap);
        }
        this.entries.delete(key);
        this.entries.set(key, entry);
        while (this.entries.size > this.maxSize) {
            const oldest = this.entries.keys().next().value as string;
            const evicted = this.entries.get(oldest);
            if (evicted?.bitmap) {
                this.release(evicted.bitmap);
            }
            this.entries.delete(oldest);
        }
    }
}
